import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, FileText, AlertCircle, Download } from 'lucide-react';
import { useCreateBulkCategory } from '@/services/mapdata/queries';
import { useAuthStore } from '@/store/auth';
import { showToast } from '@/components/ui/show-toast';
import { useNavigate } from 'react-router-dom';

interface ParsedCategory {
  name: string;
  tags: string[];
}

interface BulkUploadModalProps {
  onSuccess?: () => void;
}

export function BulkUploadModal({ onSuccess }: BulkUploadModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedCategory[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const activeWorkspace = useAuthStore(state => state.activeWorkspace);

  const createBulkCategoryMutation = useCreateBulkCategory(
    () => {
      showToast('success', 'Categories created successfully');
      setIsOpen(false);
      setParsedData([]);
      setFileName('');
      setError('');
      onSuccess?.();
      navigate('/dashboard/data');
    },
    (error: any) => {
      showToast('error', 'Failed to create categories');
      console.error('Bulk create error:', error);
    }
  );

  const parseCSV = (csvText: string): ParsedCategory[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const categories: ParsedCategory[] = [];

    headers.forEach(header => {
      if (header && header.trim()) {
        categories.push({
          name: header,
          tags: []
        });
      }
    });

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
      
      values.forEach((value, index) => {
        if (index < categories.length && value && value.trim()) {
          const trimmedValue = value.trim();
          if (!categories[index].tags.includes(trimmedValue)) {
            categories[index].tags.push(trimmedValue);
          }
        }
      });
    }

    return categories.filter(category => category.tags.length > 0);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const parsed = parseCSV(csvText);
        setParsedData(parsed);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
        setParsedData([]);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      setError('No valid data found in CSV');
      return;
    }

    try {
      await createBulkCategoryMutation.mutateAsync({
        workspace: activeWorkspace || '',
        categories: parsedData
      });
    } catch (error) {
      // Error is handled in the mutation callbacks
    }
  };

  const handleReset = () => {
    setParsedData([]);
    setFileName('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      ['Technology', 'Healthcare', 'Education', 'Finance'],
      ['Software Development', 'Telemedicine', 'Online Learning', 'Digital Banking'],
      ['Mobile Apps', 'Electronic Health Records', 'STEM Programs', 'Cryptocurrency'],
      ['AI/ML', 'Medical Devices', 'Language Learning', 'Investment Platforms'],
      ['Cloud Computing', 'Mental Health', 'Insurance Tech'],
      ['Cybersecurity', 'Telehealth', 'Fintech'],
      ['Data Analytics', 'Digital Payments'],
      ['Web Development', 'Blockchain'],
      ['DevOps']
    ];

    const csvContent = sampleData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample-categories.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('success', 'Sample CSV downloaded successfully');
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Import Dataset
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Import Dataset from CSV
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Instructions Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">How to format your CSV</h3>
            <p className="text-sm text-blue-700 mb-3">
              Each column represents a category. The top cell (row 1) of every column should be the category name, with all tags listed below it. Download the sample file to see the expected format.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadSampleCSV}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download Sample CSV
            </Button>
          </div>

          {/* File Upload Section */}
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              
              {!fileName ? (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload or drag and drop your CSV file
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose File
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <FileText className="h-8 w-8 mx-auto text-green-600" />
                  <p className="text-sm font-medium">{fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {parsedData.length} categories found
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change File
                  </Button>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          {/* Preview Section */}
          {parsedData.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Preview Data</h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {parsedData.map((category, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="mb-3">
                      <h4 className="font-medium text-primary">{category.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {category.tags.length} items
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {category.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="inline-flex items-center rounded-full bg-secondary px-2 py-1 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={createBulkCategoryMutation.isPending}
            >
              Reset
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={createBulkCategoryMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={parsedData.length === 0 || createBulkCategoryMutation.isPending}
                className="bg-primary hover:bg-primary/90"
              >
                {createBulkCategoryMutation.isPending ? 'Creating...' : `Create ${parsedData.length} Categories`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}